import eventProxy from '@/scripts/tools/eventProxy';
import { tips, getSkewTime } from '@/scripts/tools/utils';
import tabMessage from '@/scripts/common/tabMessage';
import storage from '@/scripts/tools/storage';
import oauth from './oauth';
import keyBox, { restoreKeys } from './keyBox';

let credential = {};

// bindingMap 持久化 key（sessionStorage 域，关 tab 自动失效）
// 刷新 tab 时 bindingMap 内存丢失但 STS credential 还在，必须同步落盘 + 恢复，
// 否则注入器拿不到 sig → 接入层 NO_HEADER 403 → 用户被踢。
const BINDING_MAP_KEY = 'bindingMap';
function saveBindingMap(map) {
  try {
    if (map.size === 0) {
      storage.del(BINDING_MAP_KEY, 'session');
      return;
    }
    storage.add(BINDING_MAP_KEY, [...map.entries()], 'session');
  } catch (e) {
    // 静默：最坏退化为接入层 NO_HEADER 兜底，触发一次重登
  }
}
function loadBindingMap() {
  try {
    const entries = storage.get(BINDING_MAP_KEY, 'session');
    if (Array.isArray(entries)) return new Map(entries);
  } catch (e) {
    // ignore
  }
  return new Map();
}

// OAuth binding 字段映射：tmpSecretId -> { sig, expireAt }
// 接入层 validate-oauth-binding 中间件需要请求带上对应签名 sig 的 header。
// 用 Map 而非把 sig/expireAt 直接挂在 credential 上，原因：
//   并发请求场景下，多个请求各自走 await getCredential 拿到不同代次的 credential
//   闭包；如果 sig 跟 credential 全局变量绑定，全局变量被后到的续期覆盖后，
//   先到的请求用旧 secretId 签名 + 新 sig 注入 → 服务端校验 mismatch 403。
//   按 tmpSecretId 维度建索引后，每次请求注入 header 时直接按"自己签名用的
//   secretId"查 map，跟 credential 全局变量是否被覆盖无关，并发安全。
//
// 仅 OAuth 临时密钥会写入 map（getCredential 续期路径），永久 AK（SecretKey 登录）
// 走的是另一条路径，自然不会写入。读取侧若 map 中查不到对应 secretId 返回
// undefined，注入逻辑会跳过 → 永久 AK 请求不会被错误注入 header。
//
// 内存增长：每次续期 +1 条目（STS 半小时 TTL，挂 8 小时 ≈ 16 条），可忽略。
// 不主动清理：logout 时整体清空（见 user.logout）。
//
// 初始化时从 sessionStorage 恢复（刷新 tab 场景）：旧条目对应的 STS 即便过期
// 也无碍，接入层会 EXPIRED 拒，跟内存版语义一致；新登录场景 sessionStorage
// 空，自然初始化空 Map。
const bindingMap = loadBindingMap();

const user = {
  isLogin: keyBox.isLogin,
  changeLoginMethod(keyType) {
    keyBox.setKeyType(keyType);
  },
  login({ keyType }) {
    keyBox.setKeyType(keyType);
    if (keyType === 'SecretKey') {
      tabMessage.sendMessage('loginMessage', { credential, keyType, isLogin: true });
      keyBox.addKey(credential);
      keyBox.login();
      eventProxy.emit('after-login');
    } else {
      user.getCredential().then(() => {
        keyBox.login();
        tabMessage.sendMessage('loginMessage', {
          credential,
          keyType,
          isLogin: true,
          // OAuth 路径附带当前 binding，新 tab 收到 credential 时一并写入自己的
          // bindingMap，避免新 tab 第一次发请求因没 binding 条目被拒
          binding: user.__getCurrentBindingForBroadcast(),
        });
        eventProxy.emit('after-login');
      });
    }
  },
  logout() {
    credential = null;
    bindingMap.clear();
    saveBindingMap(bindingMap); // 同步清掉持久化条目
    if (user.isLogin()) {
      tabMessage.sendMessage('logoutMessage');
    }
    keyBox.logout();
    eventProxy.emit('after-logout');
  },
  afterLogin(callback) {
    if (keyBox.isLogin() && credential && callback) {
      callback();
    }
    eventProxy.on('after-login', () => {
      if (callback) callback();
    });
  },
  afterLogout(callback) {
    if (!credential) callback();
    eventProxy.on('after-logout', () => {
      if (callback) callback();
    });
  },
  setCredential(cred) {
    credential = cred;
  },
  // 按 OAuth 临时密钥 SecretId 查询对应的 binding 凭证。
  // 供 dao.js / cos-js-sdk before-send 注入 header 时使用：
  // 调用方拿到自己签名用的 SecretId 后，调本接口拿到对应的 sig/expireAt，
  // 注入到本次请求的 header 上，保证签名与 binding 校验来自同一次续期。
  // 永久密钥 / 未命中 → 返回 undefined，调用方应跳过注入。
  getBindingBySecretId(secretId) {
    if (!secretId) return undefined;
    return bindingMap.get(secretId);
  },
  // 取当前内存中 credential 对应的 binding。用于跨 tab 广播 loginMessage 时
  // 附带 binding 给新 tab，避免新 tab 拿到 credential 后因没有 binding 条目
  // 而被接入层拒绝（直到自己续期才恢复）。
  __getCurrentBindingForBroadcast() {
    const sid = credential && (credential.tmpSecretId || credential.SecretId);
    if (!sid) return null;
    const b = bindingMap.get(sid);
    if (!b) return null;
    return { secretId: sid, sig: b.sig, expireAt: b.expireAt };
  },
  // 接收跨 tab 广播来的 binding 条目，写入本地 bindingMap，并落盘
  // 让接收 tab 自己刷新时能恢复
  __setBindingFromBroadcast(payload) {
    if (!payload || !payload.secretId || !payload.sig || !payload.expireAt) return;
    bindingMap.set(payload.secretId, { sig: payload.sig, expireAt: payload.expireAt });
    saveBindingMap(bindingMap);
  },
  async getCredential(force) {
    const keyType = keyBox.getKeyType();
    if (keyType === 'QcloudAccount') {
      if (!credential) {
        user.logout();
        return {};
      }
      const {
        clientId, clientToken, userOpenId, expiredTime,
      } = credential;
      // 强制取新的临时密钥 || 第一次获取临时密钥 || 临时密钥还有 5 分钟失效
      if (force || !expiredTime || expiredTime - getSkewTime() / 1000 < 5 * 60) {
        const params = { clientId, clientToken, userOpenId };
        const res = await oauth.getCredential(params);
        const {
          credentials, token, expiredTime: expired, bindingSig, bindingExpireAt,
        } = res;

        if (!credentials || !expired) {
          user.logout();
          // TODO: 中英文翻译
          tips.error('登录态已过期，请重新登录。');
          return {};
        }

        credential = {
          ...credential,
          ...credentials,
          ...token,
          expiredTime: expired,
        };

        // 按 SecretId 维度记录 binding 凭证。每次续期都会拿到新的
        // tmpSecretId，写入新条目；旧 secretId 的条目自然在它对应的 STS
        // 到期后失效（接入层校验 expireAt 兜底），不主动清理。
        // 后端兼容期 / 派发兜底未返回字段 → 不写入，调用方读到 undefined 跳过注入。
        const newSecretId = credentials && credentials.tmpSecretId;
        if (newSecretId && bindingSig && bindingExpireAt) {
          bindingMap.set(newSecretId, {
            sig: bindingSig,
            expireAt: bindingExpireAt,
          });
          // 同步落盘 sessionStorage，让刷新 tab 时能恢复
          saveBindingMap(bindingMap);
        }

        keyBox.setLastSession(credential);
        keyBox.triggerRememberKeys();

        return credential;
      }
      return credential;
    }

    // when (keyType === 'SecretKey')
    return credential || {};
  },
};

eventProxy.on('command.logout', user.logout);

restoreKeys().then(() => {
  keyBox.getLastSession().then(session => {
    credential = session;
    if (user.isLogin()) {
      if (!session || !(session.SecretKey || session.tmpSecretKey)) {
        user.logout();
      } else {
        eventProxy.emit('after-login');
      }
    } else {
      credential = null;
      keyBox.logout();
    }
  });
});

// 打开新窗口
tabMessage.sendMessage('newTab', 'newTab');
// 监听多窗口的通信
tabMessage.getMessage((key, newValue) => {
  if (key === 'loginMessage') {
    const {
      isLogin, keyType, credential: _credential, binding,
    } = newValue;
    if (!user.isLogin()) {
      if (isLogin) {
        user.setCredential(_credential);
        // 把广播来的 binding 写入本 tab 的 bindingMap，让本 tab 立刻就能用
        // _credential 的 tmpSecretId 命中查询，第一次请求即可注入 header
        if (binding) {
          user.__setBindingFromBroadcast(binding);
        }
        keyBox.setLastSession(_credential);
        keyBox.triggerRememberKeys();
        // setTimeout(() => {
        user.login({ keyType });
        // }, 100);
      } else {
        user.logout();
      }
    }
  } else if (key === 'logoutMessage') {
    if (user.isLogin()) {
      user.logout();
    }
  } else if (key === 'newTab') {
    // 若有新窗口打开
    // 有窗口处于登录态
    if (user.isLogin()) {
      tabMessage.sendMessage('loginMessage', {
        credential,
        keyType: keyBox.getKeyType(),
        isLogin: true,
        // OAuth 路径附带当前 binding（见上方 OAuth login 注释）
        binding: user.__getCurrentBindingForBroadcast(),
      });
    }
  }
});

export default user;
